import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "@reach/router"
import { navigate } from "gatsby";
import { Map, View, Overlay } from "ol";
import { OSM } from "ol/source";
import { Tile } from "ol/layer";
import { fromLonLat, toLonLat, transformExtent, transform } from "ol/proj";

import OpenStreetMapOverlay from "./OpenStreetMapOverlay";
import { initializeMarker } from "../helpers/openstreetmapMarker";

const OpenStreetMap = ({ listingType, listings, latitude, longitude, zoom, infoWindow, moveEvent }) => {
    const containerRef = useRef(null);
    const popupRef = useRef(null);
    const popupContentRef = useRef(null);
    const location = useLocation();

    const [map, setMap] = useState(null);
    const [overlay, setOverlay] = useState(null);
    const [listing, setListing] = useState(null);
    
    let mapInitialized = false;
    let mapIdleTimeout = null;

    /** initialize & render map */
    useEffect(() => {
        /** initialize popup */
        const overlayObj = new Overlay({
            element: popupRef.current,
            autoPan: {
                animation: {
                    duration: 250,
                },
            },
        });

        /** initialize map */
        const mapObj = new Map({
            layers: [
                new Tile({
                    source: new OSM()
                })
            ],
            overlays: [overlayObj],
            view: new View({
                center: fromLonLat([longitude, latitude]),
                zoom: zoom
            })
        });
        mapObj.setTarget(containerRef.current);

        /** attach click event listener */
        if (infoWindow) {
            mapObj.on('click', function (evt) {
                let feature = mapObj.forEachFeatureAtPixel(evt.pixel,
                    function (feature) {
                        return feature;
                    }
                );
                
                if (feature && feature.marker_content) {
                    setListing(feature.marker_content);
                    overlayObj.setPosition(evt.coordinate);
                }
            });
        }

        if (moveEvent) {
            mapObj.on('movestart', function (evt) {
                clearTimeout(mapIdleTimeout);
            });
    
            mapObj.on('moveend', function (evt) {
                if (!mapInitialized) {
                    mapInitialized = true;
                } else {
                    let mapView = mapObj.getView();
                    let extent = mapView.calculateExtent(mapObj.getSize());
                    extent = transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
                    let center = toLonLat(mapView.getCenter());
                    mapIdleTimeout = setTimeout(function () {
                        let params = new URLSearchParams(location.search);
                        params.set('sw_lng', extent[0]);
                        params.set('sw_lat', extent[1]);
                        params.set('ne_lng', extent[2]);
                        params.set('ne_lat', extent[3]);
                        params.set('center_lng', center[0]);
                        params.set('center_lat', center[1]);
                        params.set('zoom', mapView.getZoom());
                        navigate(`${location.pathname}?${params.toString()}`);
                    }, 2000);
                }
            });
        }

        listings.map(listing => initializeMarker(mapObj, listing, listingType));

        setMap(mapObj);
        setOverlay(overlayObj);

        return () => mapObj.setTarget(null);

    }, []);

    useEffect(() => {
        if (!map) return;

        map.setLayers([new Tile({
            source: new OSM()
        })]);
        map.getView().setCenter(transform([longitude, latitude], 'EPSG:4326', 'EPSG:3857'));

    }, [latitude, longitude]);

    useEffect(() => {
        if (!map) return;

        map.getView().setZoom(zoom);

    }, [zoom]);

    const closeInfoWindow = () => {
        if (!overlay) return;

        overlay.setPosition(undefined);
    }

    return (
        <React.Fragment>
            <div style={{ width: '100%', height: '100%' }} ref={containerRef}></div>
            <div className="ol-popup" ref={popupRef}>
                {listing && <div ref={popupContentRef}>
                    <OpenStreetMapOverlay 
                        listing={listing} 
                        listingType={listingType} 
                        closeInfoWindow={closeInfoWindow}
                    />
                </div>}
            </div>
        </React.Fragment>
    );

}

OpenStreetMap.propTypes = {
    listingType: PropTypes.string.isRequired,
    listings: PropTypes.arrayOf(Object).isRequired,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    zoom: PropTypes.number.isRequired,
    infoWindow: PropTypes.bool,
    moveEvent: PropTypes.bool
};

OpenStreetMap.defaultProps = {
    infoWindow: true,
    moveEvent: true
};

export default OpenStreetMap;
